import { useCallback, useEffect, useRef } from 'react'

const BACK_CLOSE_SENTINEL = '__duckDrawerBackClose'

/** Shared between the effect and the release function so an action can hand
 *  the sentinel off without racing the popstate handler. */
type SentinelControl = {
  pushed: boolean
  poppedByBack: boolean
}

/**
 * Browser/phone Back closes the overlay instead of leaving the page — the
 * controlled-`open` counterpart of DialogBackButtonCloseEffect in
 * packages/ui dialog.tsx (which watches Radix `data-state` instead).
 *
 * While `open`, a sentinel history entry sits on top of the stack; popping it
 * (Back) calls `onClose`. Closing by any other means (✕, Escape, overlay
 * click) pops the sentinel itself so the next Back works on a clean stack.
 *
 * The initial push is deferred to a macrotask so StrictMode's
 * mount→cleanup→mount in dev doesn't leave a ghost sentinel.
 *
 * Returns `releaseSentinel`: pops the sentinel WITHOUT closing the overlay,
 * resolving once the entry is actually gone. Await it before navigating away
 * from an open overlay (e.g. a Start/Continue action), otherwise the new page
 * lands on top of the sentinel and Back needs two presses to leave.
 */
export const useCloseOnHistoryBack = (open: boolean, onClose: () => void) => {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const controlRef = useRef<SentinelControl | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const control: SentinelControl = { pushed: false, poppedByBack: false }
    controlRef.current = control

    const timer = setTimeout(() => {
      if (cancelled) return
      window.history.pushState({ [BACK_CLOSE_SENTINEL]: true }, '')
      control.pushed = true
    }, 0)

    const handlePopState = () => {
      // Ignore pops that don't land us outside our sentinel — e.g. a stacked
      // dialog's sentinel popping back onto ours.
      if (!control.pushed || window.history.state?.[BACK_CLOSE_SENTINEL]) return
      control.poppedByBack = true
      control.pushed = false
      onCloseRef.current()
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      cancelled = true
      clearTimeout(timer)
      window.removeEventListener('popstate', handlePopState)
      controlRef.current = null
      // Skip the pop when a navigation already replaced the sentinel entry.
      if (control.pushed && !control.poppedByBack && window.history.state?.[BACK_CLOSE_SENTINEL]) {
        window.history.back()
      }
    }
  }, [open])

  const releaseSentinel = useCallback(
    () =>
      new Promise<void>((resolve) => {
        const control = controlRef.current
        if (control === null || !control.pushed || !window.history.state?.[BACK_CLOSE_SENTINEL]) {
          resolve()
          return
        }
        // Marked before back() so the effect's popstate handler ignores the
        // pop instead of treating it as a Back press (which would onClose).
        control.pushed = false
        const onPop = () => {
          window.removeEventListener('popstate', onPop)
          resolve()
        }
        window.addEventListener('popstate', onPop)
        window.history.back()
      }),
    []
  )

  return releaseSentinel
}
